import AbstractLanguage from './AbstractLanguage';

// The holidays screen and the Slack account an employee is tagged by in the holiday
// poll. Holiday titles are data and stay as entered.
export default class Holidays extends AbstractLanguage {
  get title() {
    return this.getText({
      EN: 'Holidays',
      RU: 'Праздники',
      UA: 'Святкові дні',
    });
  }

  get calendar_sync() {
    return this.getText({
      EN: 'Calendar sync',
      RU: 'Синхронизация календаря',
      UA: 'Синхронізація календаря',
    });
  }

  year_dates(year: number, count: number) {
    return this.getText({
      EN: `${year}: ${count} dates`,
      RU: `${year}: дат — ${count}`,
      UA: `${year}: дат — ${count}`,
    });
  }

  source(source: string) {
    switch (source) {
      case 'yaml':
        return this.getText({ EN: 'from the old yaml', RU: 'из старого yaml', UA: 'зі старого yaml' });
      case 'seed':
        return this.getText({ EN: 'initial', RU: 'начальные', UA: 'початкові' });
      case 'work.ua':
        return this.getText({ EN: 'from work.ua', RU: 'с work.ua', UA: 'з work.ua' });
      case 'manual':
        return this.getText({ EN: 'added by hand', RU: 'вручную', UA: 'вручну' });
      default:
        return source;
    }
  }

  next_year_present(year: number) {
    return this.getText({
      EN: `${year}: in the calendar`,
      RU: `${year}: есть в календаре`,
      UA: `${year}: є в календарі`,
    });
  }

  next_year_missing(year: number) {
    return this.getText({
      EN: `${year}: not added yet`,
      RU: `${year}: ещё нет`,
      UA: `${year}: ще немає`,
    });
  }

  get auto_sync_running() {
    return this.getText({
      EN: 'Pulled from work.ua daily at 11:00 until the year appears',
      RU: 'Загружается с work.ua ежедневно в 11:00, пока год не появится',
      UA: 'Автоматично з work.ua щодня об 11:00, поки рік не з’явиться',
    });
  }

  auto_sync_from(date: string) {
    return this.getText({
      EN: `Pulled from work.ua daily at 11:00, starting ${date}`,
      RU: `Загружается с work.ua ежедневно в 11:00, начиная с ${date}`,
      UA: `Автоматично з work.ua щодня об 11:00, починаючи з ${date}`,
    });
  }

  get syncing() {
    return this.getText({
      EN: 'Syncing…',
      RU: 'Синхронизация…',
      UA: 'Синхронізація…',
    });
  }

  sync_now(year: number) {
    return this.getText({
      EN: `Sync ${year} from work.ua now`,
      RU: `Синхронизировать ${year} с work.ua`,
      UA: `Синхронізувати ${year} з work.ua зараз`,
    });
  }

  sync_added(year: number, added: number, found: number) {
    return this.getText({
      EN: `${year}: added ${added} of ${found} dates`,
      RU: `${year}: добавлено ${added} из ${found}`,
      UA: `${year}: додано ${added} з ${found}`,
    });
  }

  sync_nothing_new(year: number, found: number) {
    return this.getText({
      EN: `${year}: all ${found} dates are already there`,
      RU: `${year}: все даты (${found}) уже есть`,
      UA: `${year}: усі дати (${found}) вже є`,
    });
  }

  get sync_failed() {
    return this.getText({
      EN: 'Could not sync',
      RU: 'Не удалось синхронизировать',
      UA: 'Не вдалося синхронізувати',
    });
  }

  get sync_hint() {
    return this.getText({
      EN: 'Existing dates are never overwritten. Transfers in a year from work.ua are worked out automatically, so check them below.',
      RU: 'Существующие даты не перезаписываются. Переносы для лет с work.ua вычислены автоматически — проверьте их ниже.',
      UA: 'Наявні дати не перезаписуються. Перенесення для років з work.ua вираховані автоматично — перевірте їх нижче.',
    });
  }

  get slack_poll() {
    return this.getText({
      EN: 'Slack poll',
      RU: 'Опрос в Slack',
      UA: 'Опитування в Slack',
    });
  }

  channel(name: string) {
    return this.getText({
      EN: `Channel #${name}`,
      RU: `Канал #${name}`,
      UA: `Канал #${name}`,
    });
  }

  get bot_muted() {
    return this.getText({
      EN: 'The bot is off on this server, so polls are not posted',
      RU: 'Бот выключен на этом сервере — опросы не публикуются',
      UA: 'Бот вимкнено на цьому сервері — опитування не публікуються',
    });
  }

  next_poll(title: string) {
    return this.getText({
      EN: `Next: ${title}`,
      RU: `Следующий: ${title}`,
      UA: `Наступне: ${title}`,
    });
  }

  day_off(date: string) {
    return this.getText({
      EN: `Day off ${date}`,
      RU: `Выходной ${date}`,
      UA: `Вихідний ${date}`,
    });
  }

  poll_posts_on(date: string) {
    return this.getText({
      EN: `Posts ${date} at 10:00`,
      RU: `Опубликуется ${date} в 10:00`,
      UA: `Опублікується ${date} о 10:00`,
    });
  }

  poll_overdue(date: string) {
    return this.getText({
      EN: `Was due ${date} at 10:00 but was not posted`,
      RU: `Должен был выйти ${date} в 10:00, но не опубликован`,
      UA: `Мало вийти ${date} о 10:00, але не опубліковано`,
    });
  }

  get nothing_ahead() {
    return this.getText({
      EN: 'No holidays ahead. Add next year.',
      RU: 'Впереди нет праздников — добавьте следующий год.',
      UA: 'Попереду немає свят — додайте наступний рік.',
    });
  }

  open_poll(title: string) {
    return this.getText({
      EN: `Open: ${title}`,
      RU: `Открыт: ${title}`,
      UA: `Відкрите: ${title}`,
    });
  }

  last_closed_poll(title: string) {
    return this.getText({
      EN: `Last closed: ${title}`,
      RU: `Последний закрытый: ${title}`,
      UA: `Останнє закрите: ${title}`,
    });
  }

  get holiday_deleted() {
    return this.getText({
      EN: 'holiday deleted',
      RU: 'праздник удалён',
      UA: 'свято видалено',
    });
  }

  voting_closes(time: string) {
    return this.getText({
      EN: `Voting closes ${time}`,
      RU: `Голосование закрывается ${time}`,
      UA: `Голосування закривається ${time}`,
    });
  }

  closed_at(time: string) {
    return this.getText({
      EN: `Closed ${time}`,
      RU: `Закрыт ${time}`,
      UA: `Закрито ${time}`,
    });
  }

  resting(count: number) {
    return this.getText({
      EN: `resting ${count}`,
      RU: `отдыхают ${count}`,
      UA: `відпочивають ${count}`,
    });
  }

  working(count: number) {
    return this.getText({
      EN: `working ${count}`,
      RU: `работают ${count}`,
      UA: `працюють ${count}`,
    });
  }

  no_answer(count: number) {
    return this.getText({
      EN: `no answer ${count}`,
      RU: `без ответа ${count}`,
      UA: `без відповіді ${count}`,
    });
  }

  unmapped(count: number) {
    return this.getText({
      EN: `not linked to the CRM ${count}`,
      RU: `без привязки к CRM ${count}`,
      UA: `без прив’язки до CRM ${count}`,
    });
  }

  list_title(count: number) {
    return this.getText({
      EN: `Holidays (${count})`,
      RU: `Праздники (${count})`,
      UA: `Святкові дні (${count})`,
    });
  }

  get list_hint() {
    return this.getText({
      EN: 'The poll goes out 3 business days before the day off. A holiday on a weekend is moved, and the day off or the +1 vacation day goes on the moved date.',
      RU: 'Опрос выходит за 3 рабочих дня до выходного. Если праздник выпадает на выходной, он переносится — именно на перенесённую дату ставится выходной или +1 день к отпуску.',
      UA: 'Опитування виходить за 3 робочі дні до вихідного. Якщо свято випадає на вихідний, воно переноситься — саме на перенесену дату ставиться вихідний або +1 день до відпустки.',
    });
  }

  get show_this_year() {
    return this.getText({
      EN: 'Show this year onward',
      RU: 'Показать с этого года',
      UA: 'Показати з цього року',
    });
  }

  get show_all_years() {
    return this.getText({
      EN: 'Show all years',
      RU: 'Показать все годы',
      UA: 'Показати всі роки',
    });
  }

  moved_to(date: string, observed: string) {
    return this.getText({
      EN: `${date}, moved to ${observed}`,
      RU: `${date} — перенесено на ${observed}`,
      UA: `${date} — перенесено на ${observed}`,
    });
  }

  get check_transfer() {
    return this.getText({
      EN: ' · from work.ua, check the transfer',
      RU: ' · с work.ua, проверьте перенос',
      UA: ' · з work.ua, перевірте перенесення',
    });
  }

  get holiday_name() {
    return this.getText({
      EN: 'Holiday name',
      RU: 'Название праздника',
      UA: 'Назва свята',
    });
  }

  get new_holiday() {
    return this.getText({
      EN: 'New holiday',
      RU: 'Новый праздник',
      UA: 'Нове свято',
    });
  }

  get holiday_date() {
    return this.getText({
      EN: 'Holiday date',
      RU: 'Дата праздника',
      UA: 'Дата свята',
    });
  }

  get day_off_label() {
    return this.getText({
      EN: 'Day off',
      RU: 'Выходной',
      UA: 'Вихідний',
    });
  }

  get day_off_if_moved() {
    return this.getText({
      EN: 'Day off, if moved',
      RU: 'Выходной, если перенесён',
      UA: 'Вихідний, якщо перенесено',
    });
  }

  // --- the Slack account, set on the employee page

  get link_slack() {
    return this.getText({
      EN: 'Link Slack account',
      RU: 'Привязать аккаунт Slack',
      UA: 'Прив’язати акаунт Slack',
    });
  }

  slack_linked(name: string) {
    return this.getText({
      EN: `Slack: ${name}`,
      RU: `Slack: ${name}`,
      UA: `Slack: ${name}`,
    });
  }

  get slack_account() {
    return this.getText({
      EN: 'Slack account',
      RU: 'Аккаунт в Slack',
      UA: 'Акаунт у Slack',
    });
  }

  get slack_account_hint() {
    return this.getText({
      EN: 'Needed to mention this person in the holiday poll and to count their reaction.',
      RU: 'Нужен, чтобы упоминать человека в опросе о праздниках и засчитывать его реакцию.',
      UA: 'Потрібен, щоб згадувати людину в опитуванні про святкові дні та зараховувати її реакцію.',
    });
  }

  get no_free_slack_accounts() {
    return this.getText({
      EN: 'No free Slack accounts.',
      RU: 'Нет свободных аккаунтов Slack.',
      UA: 'Немає вільних акаунтів Slack.',
    });
  }

  get current_account() {
    return this.getText({
      EN: ' · current',
      RU: ' · текущий',
      UA: ' · поточний',
    });
  }

  get unlink_slack() {
    return this.getText({
      EN: 'Unlink account',
      RU: 'Отвязать аккаунт',
      UA: 'Відв’язати акаунт',
    });
  }
}
